import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStats, useUsers } from '../../api/admin';
import { Page, PageHeader } from '../../components/Layout';
import { FilterBar, FilterSelect, SearchInput } from '../../components/FilterBar';
import { Pagination } from '../../components/Pagination';
import { ActiveBadge, Badge, RoleBadge } from '../../components/Badge';
import { UserCell } from '../../components/Avatar';
import { EmptyState, ErrorState } from '../../components/EmptyState';
import { SkeletonRows } from '../../components/Spinner';
import { CreateUserModal } from './CreateUserModal';
import { useDebounced } from '../../hooks/useDebounced';
import { formatNumber, formatRelative, shortEmail } from '../../lib/format';
import { ApiError } from '../../lib/api';
import type { UserRole, UserSort } from '../../types/api';

export function UsersPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [role, setRole] = useState<UserRole | ''>('');
  const [status, setStatus] = useState<'active' | 'suspended' | ''>('');
  const [verified, setVerified] = useState<'yes' | 'no' | ''>('');
  const [signedIn, setSignedIn] = useState<'never' | 'yes' | ''>('');
  const [sort, setSort] = useState<UserSort>('newest');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [createOpen, setCreateOpen] = useState(false);

  const debouncedSearch = useDebounced(search);

  const query = useMemo(
    () => ({
      page,
      limit,
      sort,
      q: debouncedSearch.trim() || undefined,
      role: role || undefined,
      isActive: status === '' ? undefined : status === 'active',
      isVerified: verified === '' ? undefined : verified === 'yes',
      neverSignedIn: signedIn === '' ? undefined : signedIn === 'never',
    }),
    [page, limit, sort, debouncedSearch, role, status, verified, signedIn],
  );

  const users = useUsers(query);
  // Counts for the role tabs come from the overview stats the home page already polls.
  const stats = useStats();
  const hasFilters = Boolean(debouncedSearch || role || status || verified || signedIn);

  const resetFilters = () => {
    setSearch('');
    setRole('');
    setStatus('');
    setVerified('');
    setSignedIn('');
    setPage(1);
  };

  // Any filter change puts you back on page 1 — otherwise a narrower result
  // set lands you on an empty page 4.
  const onFilter = <T,>(setter: (v: T) => void) => (value: T) => {
    setter(value);
    setPage(1);
  };

  return (
    <Page>
      <PageHeader
        title="People"
        description="Students, tutors, and administrators."
        actions={
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => setCreateOpen(true)}
          >
            Add a person
          </button>
        }
      />

      <div role="tablist" aria-label="Role" className="mb-3 flex flex-wrap gap-1.5">
        {([
          ['', 'Everyone', stats.data?.users.total],
          ['student', 'Students', stats.data?.users.students],
          ['tutor', 'Tutors', stats.data?.users.tutors],
          ['admin', 'Administrators', stats.data?.users.admins],
        ] as const).map(([value, label, count]) => (
          <button key={value || 'all'} type="button" role="tab" aria-selected={role === value}
            className={`rounded-full border px-3 py-1 text-sm ${role === value ? 'border-accent bg-accent-wash font-semibold text-accent' : 'border-rule text-ink-2 hover:bg-surface-2'}`}
            onClick={() => onFilter(setRole)(value)}>
            {label}{count !== undefined && <span className="tabular ml-1.5 text-xs">{formatNumber(count)}</span>}
          </button>
        ))}
      </div>

      <div className="panel overflow-hidden">
        <FilterBar onReset={resetFilters} hasFilters={hasFilters}>
          <SearchInput
            value={search}
            onChange={onFilter(setSearch)}
            placeholder="Search name or email"
          />
          <FilterSelect
            label="Account status"
            value={status}
            onChange={onFilter(setStatus)}
            placeholder="Any status"
            options={[
              { value: 'active', label: 'Active' },
              { value: 'suspended', label: 'Suspended' },
            ]}
          />
          <FilterSelect
            label="Tutor verification"
            value={verified}
            onChange={onFilter(setVerified)}
            placeholder="Any verification"
            options={[
              { value: 'yes', label: 'Verified tutors' },
              { value: 'no', label: 'Unverified tutors' },
            ]}
          />
          <FilterSelect
            label="Sign-in"
            value={signedIn}
            onChange={onFilter(setSignedIn)}
            placeholder="Any sign-in"
            options={[
              { value: 'never', label: 'Never signed in' },
              { value: 'yes', label: 'Has signed in' },
            ]}
          />
          <select
            className="select ml-auto h-[2.125rem] w-auto"
            value={sort}
            aria-label="Sort by"
            onChange={(e) => setSort(e.target.value as UserSort)}
          >
            <option value="newest">Newest first</option>
            <option value="oldest">Oldest first</option>
            <option value="name">Name A–Z</option>
            <option value="lastLogin">Last seen</option>
          </select>
        </FilterBar>

        {users.isError ? (
          <ErrorState
            message={users.error instanceof ApiError ? users.error.message : undefined}
            onRetry={() => void users.refetch()}
          />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Person</th>
                    <th>Role</th>
                    <th>Account</th>
                    <th className="text-right">Lessons</th>
                    <th>Last seen</th>
                    <th>Joined</th>
                  </tr>
                </thead>
                <tbody>
                  {users.isLoading ? (
                    <SkeletonRows rows={8} cols={6} />
                  ) : users.data && users.data.data.length > 0 ? (
                    users.data.data.map((u) => (
                      <tr
                        key={u.id}
                        data-clickable="true"
                        onClick={() => navigate(`/users/${u.id}`)}
                      >
                        <td>
                          <UserCell
                            name={u.fullName}
                            secondary={shortEmail(u.email)}
                            secondaryTitle={u.email}
                            src={u.avatarUrl}
                            trailing={
                              u.isOnline ? (
                                <span className="pulse-dot" role="img" aria-label="Online" />
                              ) : null
                            }
                          />
                        </td>
                        <td>
                          <div className="flex items-center gap-1.5">
                            <RoleBadge role={u.role} />
                            {u.role === 'tutor' && u.tutorProfile?.isVerified && (
                              <Badge tone="good">Verified</Badge>
                            )}
                            {/* A flag to look at, not a verdict — a student
                                with a broken phone and a borrowed one looks
                                the same for a day. Neutral, not danger. */}
                            {u.sharingSuspected && <Badge>Sharing?</Badge>}
                          </div>
                        </td>
                        <td>
                          <ActiveBadge isActive={u.isActive} />
                        </td>
                        <td className="tabular text-right">
                          <span className="text-ink">{formatNumber(u.sessionCount)}</span>
                          {u.upcomingSessionCount > 0 && (
                            <span className="ml-1 text-xs text-ink-3">
                              ({u.upcomingSessionCount} upcoming)
                            </span>
                          )}
                        </td>
                        <td className="text-xs text-ink-3">
                          {u.lastLoginAt ? (
                            <span title={new Date(u.lastLoginAt).toLocaleString()}>
                              {formatRelative(u.lastLoginAt)}
                            </span>
                          ) : (
                            'Never signed in'
                          )}
                        </td>
                        <td className="text-xs text-ink-3">
                          <span title={new Date(u.createdAt).toLocaleString()}>
                            {formatRelative(u.createdAt)}
                          </span>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={6} className="border-b-0 p-0">
                        <EmptyState
                          title={hasFilters ? 'No one matches these filters' : 'No people yet'}
                          description={
                            hasFilters
                              ? 'Try a broader search, or clear the filters.'
                              : 'Add the first student or tutor to get started.'
                          }
                          action={
                            hasFilters ? (
                              <button
                                type="button"
                                className="btn btn-secondary btn-sm"
                                onClick={resetFilters}
                              >
                                Clear filters
                              </button>
                            ) : (
                              <button
                                type="button"
                                className="btn btn-primary btn-sm"
                                onClick={() => setCreateOpen(true)}
                              >
                                Add a person
                              </button>
                            )
                          }
                        />
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {users.data && users.data.data.length > 0 && (
              <Pagination
                meta={users.data.meta}
                onPageChange={setPage}
                onLimitChange={(l) => {
                  setLimit(l);
                  setPage(1);
                }}
              />
            )}
          </>
        )}
      </div>

      <CreateUserModal open={createOpen} onClose={() => setCreateOpen(false)} />
    </Page>
  );
}
